import _cloneDeep from 'lodash.clonedeep'

import types from './types'
import { smartChat } from 'api'
import { addLabelToProcess, getProcessDetail } from '../details/actions'
import settingsAct from '../../settings/actions'

function getMaxProcessOrder(processList = []) {
  const nums = processList.reduce((prev, next) => {
    if (next.processType === "customer_alternative_branch") return prev
    if (typeof next.processOrder !== 'number') return prev

    prev.push(next.processOrder)

    return prev
  }, [])

  if (!nums.length) return 1

  return Math.max(...nums) + 1
}

const getSmartchat = (payload = {}) => async (dispatch, getStore) => {
  const { onDepth } = payload
  const {
    companies,
    smartchatHeader: { error }
  } = getStore()

  if (error) dispatch(updateError(null))
  if (!onDepth) dispatch(updateLoading(true))

  try {
    const { data: header } = await smartChat.getHeader()
    const { data: processesList } = await smartChat.getProcessesList(header._id)
    const { data: { result: categoriesList } } = await smartChat.getProcessCategoriesList(companies._id)
    const { data: { result: labelsList } } = await smartChat.getLabelsList(companies._id)

    let currentProcess = ''
    const storagedCurrentProcess = sessionStorage.getItem('smartchat_currentProcess') || ''
    const process = processesList.find(process => process._id === storagedCurrentProcess)
    if (process) {
      currentProcess = storagedCurrentProcess
    } else {
      currentProcess = processesList.find(process => process.isDefault)?._id || ''
    }

    dispatch({
      type: types.SMARTCHAT_HEADER_INITIAL_DATA,
      payload: { header, processesList, categoriesList, labelsList, currentProcess }
    })
  } catch (error) {
    dispatch(updateError({
      origin: '[Redux] getSmartchat',
      message: 'chatbot_error_message_back'
    }))
  }

  if (!onDepth) dispatch(updateLoading(false))
}

const deleteSmartchat = () => ({
  type: types.SMARTCHAT_HEADER_INITIAL_DATA,
  payload: { header: {}, processesList: [], categoriesList: [], currentProcess: '', currentTab: 'processes' }
})

const reloadSmartchat = (payload = {}) => async (dispatch) => {
  const { onDepth } = payload

  await dispatch(getSmartchat({ onDepth }))
  dispatch(getProcessDetail({ onDepth }))
}

const resetSmartchat = (payload = {}) => async (dispatch, getStore) => {
  const { type, onFinish } = payload
  const { header, modalError } = getStore().smartchatHeader

  if (modalError) dispatch(updateModalError(null))
  dispatch(updateModalLoading(true))

  try {
    let result = {}
    if (type === 'default') result = await smartChat.setDefault({ headerId: header._id })
    if (type === 'restore') result = await smartChat.setEarlierVersion({ headerId: header._id })

    if (result.status === 200) {
      onFinish && typeof onFinish === 'function' && onFinish()
      dispatch(reloadSmartchat({ onDepth: false }))
    }
  } catch (error) {
    dispatch(updateModalError({
      origin: '[Redux] resetSmartchat',
      message: 'smartchat_error_default_restore'
    }))
  }

  dispatch(updateModalLoading(false))
}

const deploySmartchat = (payload = {}) => async (dispatch, getStore) => {
  const { onFinish } = payload
  const { header, deployError } = getStore().smartchatHeader

  if (deployError) dispatch(updateDeployError(null))
  dispatch(updateDeployLoading(true))

  try {
    const result = await smartChat.deploy({ headerId: header._id })
    if (result.status === 200) {
      const _header = _cloneDeep(header)
      _header.applyPending = false

      dispatch({
        type: types.SMARTCHAT_HEADER_INITIAL_DATA,
        payload: { header: _header }
      })

      if (onFinish && typeof onFinish === 'function') {
        onFinish(true)
        setTimeout(() => onFinish(false), 3000)
      }
      dispatch(reloadSmartchat({ onDepth: true }))
    }
  } catch (error) {
    dispatch(updateDeployError({
      origin: '[Redux] deploySmartchat',
      message: 'smartchat_error_default_restore'
    }))

    onFinish && typeof onFinish === 'function' && onFinish(true)
  }

  dispatch(updateDeployLoading(false))
}

const createProcess = (payload = {}) => async (dispatch, getStore) => {
  const { processType, categoryId, processName, processColor, copyFrom, onFinish } = payload
  const { header, processesList, modalError } = getStore().smartchatHeader

  if (modalError) dispatch(updateModalError(null))
  dispatch(updateModalLoading(true))

  if (!processName || !processColor) {
    dispatch(updateModalError({
      origin: '[Redux] createProcess',
      message: 'dashboard_all-required-field'
    }))
    dispatch(updateModalLoading(false))
    return
  }

  const { data: processesNames } = await smartChat.getProcessesPartialList(header._id, {
    processType: ['assistant', 'conversational', 'transactional']
  })
  if (processesNames.find(process => process.processName === processName)) {
    dispatch(updateModalError({
      origin: '[Redux] createProcess',
      message: 'dashboard_error_process_duplicate_name'
    }))
    dispatch(updateModalLoading(false))
    return
  }

  try {
    const result = await smartChat.process({
      process: {
        processType,
        category: categoryId,
        processName,
        processColor,
        processOrder: getMaxProcessOrder(processesList)
      },
      copyFrom,
      headerId: header._id
    })
    if (result.status === 200) {
      const _processesList = _cloneDeep(processesList)
      _processesList.push(result.data)

      dispatch({
        type: types.SMARTCHAT_HEADER_INITIAL_DATA,
        payload: { processesList: _processesList }
      })

      dispatch(updateCurrentProcess(result.data._id))
      onFinish && typeof onFinish === 'function' && onFinish()
      dispatch(getSmartchat({ onDepth: true }))
    }
  } catch (error) {
    if (error.data.message) {
      if (error.data.message === 'Process status is inconsistent') {
        dispatch(updateModalError({
          origin: '[Redux] createProcess',
          message: 'smartchat_error_process_origin_inconsistent'
        }))
      }
    } else {
      dispatch(updateModalError({
        origin: '[Redux] createProcess',
        message: `chatbot_error_processes_add`
      }))
    }
  }

  dispatch(updateModalLoading(false))
}

const updateProcess = (payload = {}) => async (dispatch, getStore) => {
  const { processId, processName, processColor, onFinish } = payload
  const { processesList } = getStore().smartchatHeader

  if (!processName || !processColor) {
    dispatch(updateModalError({
      origin: '[Redux] updateProcess',
      message: 'dashboard_all-required-field'
    }))
    dispatch(updateModalLoading(false))
    return
  }

  if (processesList.find(process => process.processName === processName && process._id !== processId)) {
    dispatch(updateModalError({
      origin: '[Redux] updateProcess',
      message: 'dashboard_error_process_duplicate_name'
    }))
    dispatch(updateModalLoading(false))
    return
  }

  dispatch(partialUpdateProcess({
    processId,
    data: { processName, processColor },
    refresh: true,
    onFinish
  }))
}

const deleteProcess = (payload = {}) => async (dispatch, getStore) => {
  const { processId, onFinish } = payload
  const { processesList, modalError } = getStore().smartchatHeader

  if (modalError) dispatch(updateModalError(null))
  dispatch(updateModalLoading(true))

  try {
    const result = await smartChat.deleteProcess(processId)
    if (result.status === 200) {
      if (result.data.message?.includes('process has a startProcess or managementList reference')) {
        dispatch(updateModalError({
          origin: '[Redux] deleteProcess',
          message: 'chatbot_error_processes_processReferenced'
        }))
        dispatch(updateModalLoading(false))
        return
      }

      const _processesList = _cloneDeep(processesList)
      const processIndex = _processesList.findIndex(process => process._id === processId)
      _processesList.splice(processIndex, 1)

      dispatch({
        type: types.SMARTCHAT_HEADER_INITIAL_DATA,
        payload: { processesList: _processesList }
      })
      dispatch(updateCurrentProcess(_processesList.find(process => process.isDefault)?._id || ''))

      onFinish && typeof onFinish === 'function' && onFinish()
      dispatch(settingsAct.remove(processId))
      dispatch(getSmartchat({ onDepth: true }))
    }
  } catch (error) {
    dispatch(updateModalError({
      origin: '[Redux] deleteProcess',
      message: 'chatbot_error_processes_delete'
    }))
  }

  dispatch(updateModalLoading(false))
}

const updateCurrentProcess = (payload) => (dispatch, getStore) => {
  const { currentProcess } = getStore().smartchatHeader
  if (payload === currentProcess) return

  dispatch({
    type: types.SMARTCHAT_HEADER_UPDATE_CURRENT_PROCESS,
    payload
  })
  sessionStorage.setItem('smartchat_currentProcess', payload)
}

const updateCurrentTab = (payload) => ({
  type: types.SMARTCHAT_HEADER_UPDATE_CURRENT_TAB,
  payload
})

const createProcessCategory = (payload = {}) => async (dispatch, getStore) => {
  const { category, onFinish } = payload
  const { categoriesList, modalError } = getStore().smartchatHeader

  if (modalError) dispatch(updateModalError(null))
  dispatch(updateModalLoading(true))

  if (!category.name || !category.color) {
    dispatch(updateModalError({
      origin: '[Redux] createProcessCategory',
      message: 'dashboard_all-required-field'
    }))
    dispatch(updateModalLoading(false))
    return
  }

  if (categoriesList.find(savedCategory => savedCategory.name === category.name)) {
    dispatch(updateModalError({
      origin: '[Redux] createProcessCategory',
      message: 'dashboard_error_category_duplicate_name'
    }))
    dispatch(updateModalLoading(false))
    return
  }

  try {
    const result = await smartChat.createProcessCategory(category)
    if (result.status === 200) {
      const _categoriesList = _cloneDeep(categoriesList)
      _categoriesList.push({
        ...category,
        _id: result.data.id
      })

      dispatch({
        type: types.SMARTCHAT_HEADER_UPDATE_CATEGORIES_LIST,
        payload: _categoriesList
      })

      onFinish && typeof onFinish === 'function' && onFinish()
    }
  } catch (error) {
    dispatch(updateModalError({
      origin: '[Redux] createProcessCategory',
      message: 'smartchat_error_category_create'
    }))
  }

  dispatch(updateModalLoading(false))
}

const updateProcessCategory = (payload = {}) => async (dispatch, getStore) => {
  const { categoryId, category, onFinish } = payload
  const { categoriesList, modalError } = getStore().smartchatHeader

  if (modalError) dispatch(updateModalError(null))
  dispatch(updateModalLoading(true))

  if (!category.name || !category.color) {
    dispatch(updateModalError({
      origin: '[Redux] updateProcessCategory',
      message: 'dashboard_all-required-field'
    }))
    dispatch(updateModalLoading(false))
    return
  }

  if (categoriesList.find(savedCategory => savedCategory.name === category.name && savedCategory._id !== categoryId)) {
    dispatch(updateModalError({
      origin: '[Redux] updateProcessCategory',
      message: 'dashboard_error_category_duplicate_name'
    }))
    dispatch(updateModalLoading(false))
    return
  }

  try {
    const result = await smartChat.updateProcessCategory(categoryId, {
      name: category.name,
      color: category.color,
      order: category.order
    })
    if (result.status === 200) {
      const _categoriesList = _cloneDeep(categoriesList)
      const categoryIndex = _categoriesList.findIndex(savedCategory => savedCategory._id === categoryId)
      _categoriesList[categoryIndex] = category

      dispatch({
        type: types.SMARTCHAT_HEADER_UPDATE_CATEGORIES_LIST,
        payload: _categoriesList
      })

      onFinish && typeof onFinish === 'function' && onFinish()
    }
  } catch (error) {
    dispatch(updateModalError({
      origin: '[Redux] updateProcessCategory',
      message: 'smartchat_error_category_update'
    }))
  }

  dispatch(updateModalLoading(false))
}

const deleteProcessCategory = (payload = {}) => async (dispatch, getStore) => {
  const { categoryId, onFinish } = payload
  const { categoriesList, modalError } = getStore().smartchatHeader

  if (modalError) dispatch(updateModalError(null))
  dispatch(updateModalLoading(true))

  try {
    const result = await smartChat.deleteProcessCategory(categoryId)
    if (result.status === 200) {
      const _categoriesList = _cloneDeep(categoriesList)
      const categoryIndex = _categoriesList.findIndex(savedCategory => savedCategory._id === categoryId)
      _categoriesList.splice(categoryIndex, 1)

      dispatch({
        type: types.SMARTCHAT_HEADER_UPDATE_CATEGORIES_LIST,
        payload: _categoriesList
      })

      onFinish && typeof onFinish === 'function' && onFinish()
      dispatch(getSmartchat({ onDepth: true }))
    }
  } catch (error) {
    dispatch(updateModalError({
      origin: '[Redux] deleteProcessCategory',
      message: 'smartchat_error_category_delete'
    }))
  }

  dispatch(updateModalLoading(false))
}

const createLabel = (payload = {}) => async (dispatch, getStore) => {
  const { label, onFinish } = payload
  const { labelsList, modalError } = getStore().smartchatHeader

  if (modalError) dispatch(updateModalError(null))
  dispatch(updateModalLoading(true))

  if (!label.name || !label.color) {
    dispatch(updateModalError({
      origin: '[Redux] createLabel',
      message: 'dashboard_all-required-field'
    }))
    dispatch(updateModalLoading(false))
    return
  }

  if (labelsList.find(savedLabel => savedLabel.name === label.name)) {
    dispatch(updateModalError({
      origin: '[Redux] createLabel',
      message: 'dashboard_error_label_duplicate_name'
    }))
    dispatch(updateModalLoading(false))
    return
  }

  try {
    const result = await smartChat.createLabel(label)
    if (result.status === 200) {
      const _labelsList = _cloneDeep(labelsList)
      _labelsList.push({
        ...label,
        _id: result.data.id
      })

      dispatch({
        type: types.SMARTCHAT_HEADER_UPDATE_LABELS_LIST,
        payload: _labelsList
      })

      onFinish && typeof onFinish === 'function' && onFinish()
    }
  } catch (error) {
    dispatch(updateModalError({
      origin: '[Redux] createLabel',
      message: 'smartchat_error_label_create'
    }))
  }

  dispatch(updateModalLoading(false))
}

const updateLabel = (payload = {}) => async (dispatch, getStore) => {
  const { labelId, label, onFinish } = payload
  const { labelsList, modalError } = getStore().smartchatHeader

  if (modalError) dispatch(updateModalError(null))
  dispatch(updateModalLoading(true))

  if (!label.name || !label.color) {
    dispatch(updateModalError({
      origin: '[Redux] updateLabel',
      message: 'dashboard_all-required-field'
    }))
    dispatch(updateModalLoading(false))
    return
  }

  if (labelsList.find(savedLabel => savedLabel.name === label.name && savedLabel._id !== labelId)) {
    dispatch(updateModalError({
      origin: '[Redux] updateLabel',
      message: 'dashboard_error_label_duplicate_name'
    }))
    dispatch(updateModalLoading(false))
    return
  }

  try {
    const result = await smartChat.updateLabel(labelId, {
      name: label.name,
      color: label.color
    })
    if (result.status === 200) {
      const _labelsList = _cloneDeep(labelsList)
      const labelIndex = _labelsList.findIndex(savedLabel => savedLabel._id === labelId)
      _labelsList[labelIndex] = label

      dispatch({
        type: types.SMARTCHAT_HEADER_UPDATE_LABELS_LIST,
        payload: _labelsList
      })

      onFinish && typeof onFinish === 'function' && onFinish()
    }
  } catch (error) {
    dispatch(updateModalError({
      origin: '[Redux] updateLabel',
      message: 'smartchat_error_label_update'
    }))
  }

  dispatch(updateModalLoading(false))
}

const deleteLabel = (payload = {}) => async (dispatch, getStore) => {
  const { labelId, onFinish } = payload
  const { labelsList, modalError } = getStore().smartchatHeader

  if (modalError) dispatch(updateModalError(null))
  dispatch(updateModalLoading(true))

  try {
    const result = await smartChat.deleteLabel(labelId)
    if (result.status === 200) {
      const _labelsList = _cloneDeep(labelsList)
      const labelIndex = _labelsList.findIndex(savedLabel => savedLabel._id === labelId)
      _labelsList.splice(labelIndex, 1)

      dispatch({
        type: types.SMARTCHAT_HEADER_UPDATE_LABELS_LIST,
        payload: _labelsList
      })

      onFinish && typeof onFinish === 'function' && onFinish()
    }
  } catch (error) {
    dispatch(updateModalError({
      origin: '[Redux] deleteLabel',
      message: 'error_delete_label'
    }))
  }

  dispatch(updateModalLoading(false))
}

const addProcessLabel = (payload = {}) => async (dispatch, getStore) => {
  const { processId, labelId } = payload
  const { processesList } = getStore().smartchatHeader

  const processIndex = processesList.findIndex(process => process._id === processId)
  const _labels = _cloneDeep(processesList[processIndex].labels)
  _labels.push(labelId)

  dispatch(partialUpdateProcess({
    processId,
    data: { labels: _labels },
    refresh: false
  }))
}

const deleteProcessLabel = (payload = {}) => async (dispatch, getStore) => {
  const { processId, labelId } = payload
  const { processesList } = getStore().smartchatHeader

  const processIndex = processesList.findIndex(process => process._id === processId)
  const _labels = _cloneDeep(processesList[processIndex].labels)
  const labelIndex = _labels.findIndex(label => label === labelId)
  _labels.splice(labelIndex, 1)

  dispatch(partialUpdateProcess({
    processId,
    data: { labels: _labels },
    refresh: false
  }))
}

const partialUpdateProcess = (payload = {}) => async (dispatch, getStore) => {
  const { processId, data, refresh, onFinish, onFinishNotification } = payload
  const { processesList, modalError } = getStore().smartchatHeader

  if (modalError) dispatch(updateModalError(null))
  dispatch(updateModalLoading(true))

  try {
    const result = await smartChat.processPartialUpdate({
      processId: processId,
      ...data
    })

    if (result.status === 200) {
      if (result.data.result === -1) {
        dispatch(updateModalError({
          origin: '[Redux] partialUpdateProcess',
          message: result.data.message
        }))
        dispatch(updateModalLoading(false))
        onFinishNotification && typeof onFinishNotification === 'function' && onFinishNotification()
        return
      }

      const _processesList = _cloneDeep(processesList)
      const processIndex = _processesList.findIndex(process => process._id === processId)
      _processesList[processIndex] = { ..._processesList[processIndex], ...data }

      dispatch({
        type: types.SMARTCHAT_HEADER_INITIAL_DATA,
        payload: { processesList: _processesList }
      })

      //SEND LABELS TO STORE
      dispatch(addLabelToProcess(data))

      onFinish && typeof onFinish === 'function' && onFinish()
      if (refresh) dispatch(getSmartchat({ onDepth: true }))
    }
  } catch (error) {
    dispatch(updateModalError({
      origin: '[Redux] partialUpdateProcess',
      message: 'chatbot_error_processes_update'
    }))
  }

  dispatch(updateModalLoading(false))
}

const updateDeployLoading = (payload) => ({
  type: types.SMARTCHAT_HEADER_UPDATE_DEPLOY_LOADING,
  payload
})

const updateDeployError = (payload) => ({
  type: types.SMARTCHAT_HEADER_UPDATE_DEPLOY_ERROR,
  payload
})

const updateModalLoading = (payload) => ({
  type: types.SMARTCHAT_HEADER_UPDATE_MODAL_LOADING,
  payload
})

const updateModalError = (payload) => ({
  type: types.SMARTCHAT_HEADER_UPDATE_MODAL_ERROR,
  payload
})

const updateLoading = (payload) => ({
  type: types.SMARTCHAT_HEADER_UPDATE_LOADING,
  payload
})

const updateError = (payload) => ({
  type: types.SMARTCHAT_HEADER_UPDATE_ERROR,
  payload
})

export {
  getSmartchat,
  deleteSmartchat,
  reloadSmartchat,
  resetSmartchat,
  deploySmartchat,
  createProcess,
  updateProcess,
  deleteProcess,
  updateCurrentProcess,
  updateCurrentTab,
  createProcessCategory,
  updateProcessCategory,
  deleteProcessCategory,
  createLabel,
  updateLabel,
  deleteLabel,
  addProcessLabel,
  deleteProcessLabel,
  partialUpdateProcess,
  updateDeployLoading,
  updateDeployError,
  updateModalLoading,
  updateModalError,
  updateLoading,
  updateError
}
